import React, { useMemo } from 'react';
import {
  DRUNK_LETTER_ANIMATIONS,
  type DrunkLetterAnimation,
} from '../../../constants/drunkVisionAnimations';

interface DrunkQuestionTextProps {
  text: string;
  className?: string;
  /** Approximate fraction of letters that get an animation (0–1). Default 0.45 so more letters animate */
  animateFraction?: number;
}

/** Picks a deterministic "random" animation index from a string seed (for stability per question). */
function seededPick(seed: string, max: number): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h) % max;
}

export const DrunkQuestionText: React.FC<DrunkQuestionTextProps> = ({
  text,
  className = '',
  animateFraction = 0.45,
}) => {
  const letters = useMemo(() => {
    const arr: Array<{ char: string; animation: DrunkLetterAnimation | null; delay: number }> = [];
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const seed = `${text}-${i}`;
      // Never animate spaces or other whitespace – wrapping them in inline-block spans
      // collapses the gap and makes words run together (e.g. "haveexperienced").
      const isWhitespace = /\s/.test(char);
      const shouldAnimate = !isWhitespace && (seededPick(seed, 100) / 100) < animateFraction;
      const animIndex = seededPick(seed + 'a', DRUNK_LETTER_ANIMATIONS.length);
      const animation = shouldAnimate ? DRUNK_LETTER_ANIMATIONS[animIndex] : null;
      const delay = (seededPick(seed + 'd', 16) / 10); // 0 to 1.5s
      arr.push({ char, animation, delay });
    }
    return arr;
  }, [text, animateFraction]);

  return (
    <h1 className={`${className} drunk-question-text`}>
      {letters.map(({ char, animation, delay }, i) =>
        animation ? (
          <span
            key={i}
            className={`drunk-letter ${animation}`}
            style={{ animationDelay: `${delay}s` }}
          >
            {char}
          </span>
        ) : (
          <span key={i}>{char}</span>
        )
      )}
    </h1>
  );
};
